import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaUsers, FaTicketAlt, FaCreditCard, FaCheck, FaTimes, FaUserSlash } from "react-icons/fa";
import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import PersonnelModal from "Components/PersonnelModal";
import {
  catchError,
  changeState,
  commonModalSetting,
  enterFn,
} from "JS/common";
import { getUserList, deactivateUsers } from "JS/API";
import { useMarkMenuSeen } from "Contexts/NewCountContext";
import PaymentModal from "Components/PaymentModal";
import CreateCouponModal from "Components/CreateCouponModal";
import { listPageStyles } from "utils/designSystem";

const Manage = () => {
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });
  const [searchTxt, setSearchTxt] = useState("");
  const [select, setSelect] = useState("all");
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [infoModal, setInfoModal] = useState(false); // 회원 상세보기 모달
  const [user, setUser] = useState([]);
  const [id, setId] = useState("");
  const [userPk, setUserPk] = useState("");

  const [payModal, setPayModal] = useState(false); // 결제 모달
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const [isOpenCouponModal, setIsOpenCouponModal] = useState(false)

  // 확인 모달(confirm)에서 "확인" 클릭 시 실행할 동작
  const [confirmFn, setConfirmFn] = useState(null);

  let prevent = false;
  const navigate = useNavigate();
  useMarkMenuSeen("member");

  // 스타일 정의
  const styles = {
    container: listPageStyles.container,
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    title: listPageStyles.title,
    controlsContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      minWidth: 0
    },
    select: listPageStyles.select,
    searchInput: {
      padding: '8px 12px',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      fontSize: '14px',
      minWidth: '180px',
      color: '#1e293b',
      flexShrink: 1
    },
    searchButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    couponButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    deactivateButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    tableContainer: listPageStyles.tableContainer,
    tableWrapper: listPageStyles.tableWrapper,
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '800px'
    },
    th: listPageStyles.th,
    td: listPageStyles.td,
    checkbox: listPageStyles.checkbox,
    statusActive: {
      color: '#059669',
      fontWeight: '600'
    },
    statusInactive: {
      color: '#dc2626',
      fontWeight: '600'
    },
    paymentButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      backgroundColor: '#ffffff',
      color: '#3b82f6',
      border: '1px solid #3b82f6',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    phoneNumber: {
      fontFamily: 'monospace',
      fontSize: '13px'
    }
  };

  //= 회원 목록 불러오기
  const userList = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);
    const result = await getUserList(pageInfo, select, searchTxt);
    if (typeof result === "object") {
      setUser(result?.data?.data);
      changeState(setPageInfo, "totalPage", result?.data?.meta?.total_page);
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 회원 검색
  const userSearch = async () => {
    if (searchTxt.trim() !== "" && select === "all")
      return commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "검색하실 종류를 선택해 주세요."
      );
    const result = await getUserList(pageInfo, select, searchTxt);
    if (typeof result === "object") {
      setUser(result?.data?.data);
      changeState(setPageInfo, "totalPage", result?.data?.meta?.total_page);
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 선택 회원 탈퇴(비활성화) 처리
  const runDeactivate = async () => {
    const result = await deactivateUsers(selectedUserIds);
    if (typeof result === "object") {
      const deactivated = result?.data?.deactivated ?? 0;
      setSelectedUserIds([]);
      await userList();
      return commonModalSetting(
        setAlertBox,
        true,
        "alert",
        `${deactivated}명의 회원이 탈퇴 처리되었습니다.`
      );
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 회원 탈퇴 버튼 클릭 → 확인 모달 노출
  const onClickDeactivate = () => {
    if (!selectedUserIds.length) {
      return commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "탈퇴 처리할 회원을 선택하세요."
      );
    }
    setConfirmFn(() => runDeactivate);
    commonModalSetting(
      setAlertBox,
      true,
      "confirm",
      `선택한 회원 ${selectedUserIds.length}명을 탈퇴 처리하시겠습니까?<br/>탈퇴 처리된 회원은 로그인할 수 없습니다.`
    );
  };

  //= 회원 목록 테이블 렌더
  const renderUserList = () => {
    return user.map(
      ({ id, user_id, name, created_at, is_active, phone, email }) => {
        const isChecked = selectedUserIds.includes(id);

        const handleCheckboxChange = (e) => {
          e.stopPropagation();
          if (e.target.checked) {
            setSelectedUserIds((prev) => [...prev, id]);
          } else {
            setSelectedUserIds((prev) => prev.filter((uid) => uid !== id));
          }
        };

        const onModal = () => {
          setId(id);
          setInfoModal(true);
        };

        const onPayModal = (e) => {
          e.stopPropagation();
          setUserPk(id);
          setPayModal(true);
        };

        return (
          <tr 
            key={id}
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f8fafc'}
            onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
          >
            <td style={styles.td} onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                style={styles.checkbox}
              />
            </td>
            <td style={styles.td} onClick={onModal}>{user_id}</td>
            <td style={styles.td} onClick={onModal}>{name}</td>
            <td style={styles.td} onClick={onModal}>{email}</td>
            <td style={{...styles.td, ...styles.phoneNumber}} onClick={onModal}>
              {phone.slice(0, 3)}-{phone.slice(3, 7)}-{phone.slice(7)}
            </td>
            <td 
              style={{
                ...styles.td,
                ...(is_active ? styles.statusActive : styles.statusInactive)
              }} 
              onClick={onModal}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {is_active ? <FaCheck size={12} /> : <FaTimes size={12} />}
                {is_active ? "회원" : "탈퇴"}
              </span>
            </td>
            <td style={styles.td} onClick={onModal}>{created_at.split("T")[0]}</td>
            <td style={styles.td}>
              <button
                style={styles.paymentButton}
                onClick={onPayModal}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#3b82f6';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.color = '#3b82f6';
                }}
              >
                <FaCreditCard size={12} />
                결제 요청
              </button>
            </td>
          </tr>
        );
      }
    );
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 회원 관리";
  }, []);

  useEffect(() => {
    userList();
  }, [pageInfo.page, pageInfo.limit]);

  // 모달이 닫히면 대기 중이던 확인 동작 초기화 (취소 시 stale 콜백 방지)
  useEffect(() => {
    if (!alertBox.bool) setConfirmFn(null);
  }, [alertBox.bool]);

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaUsers />
          MEMBER
        </h1>
        
        <div style={styles.controlsContainer} className="controls-container">
          <select
            value={pageInfo.limit}
            onChange={(e) => {
              setPageInfo((prev) => {
                const clone = { ...prev };
                clone.page = 1;
                clone.limit = e.target.value;
                return clone;
              });
            }}
            style={styles.select}
          >
            <option value="10">10명씩 보기</option>
            <option value="30">30명씩 보기</option>
            <option value="50">50명씩 보기</option>
          </select>
          
          <select 
            value={select} 
            onChange={(e) => setSelect(e.target.value)}
            style={styles.select}
          >
            <option value="all">검색유형</option>
            <option value="name">이름</option>
            <option value="uid">아이디</option>
            <option value="phone">전화번호</option>
          </select>
          
          <input
            type="text"
            placeholder="검색어를 입력하세요."
            value={searchTxt}
            onChange={(e) => setSearchTxt(e.target.value)}
            onKeyDown={(e) => {
              if (!alertBox.bool) enterFn(e, userSearch);
            }}
            style={styles.searchInput}
            className="search-input"
          />
          
          <button 
            style={styles.searchButton}
            onClick={userSearch}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            <FaSearch />
            <span className="button-text">검색</span>
          </button>
          
          <button
            style={styles.couponButton}
            onClick={() => {
              if (!selectedUserIds.length) {
                return commonModalSetting(
                  setAlertBox,
                  true,
                  "alert",
                  "쿠폰을 생성할 회원을 선택하세요."
                );
              }
              setIsOpenCouponModal(true)
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
          >
            <FaTicketAlt />
            <span className="button-text">쿠폰 생성</span>
          </button>

          <button
            style={styles.deactivateButton}
            onClick={onClickDeactivate}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
          >
            <FaUserSlash />
            <span className="button-text">회원 탈퇴</span>
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div style={styles.tableContainer}>
        <div style={styles.tableWrapper} className="table-wrapper">
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>
                  <input
                    type="checkbox"
                    checked={selectedUserIds.length === user.length && user.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUserIds(user.map((u) => u.id));
                      } else {
                        setSelectedUserIds([]);
                      }
                    }}
                    style={styles.checkbox}
                  />
                </th>
                <th style={styles.th}>아이디</th>
                <th style={styles.th}>이름</th>
                <th style={styles.th}>이메일</th>
                <th style={styles.th}>전화번호</th>
                <th style={styles.th}>회원구분</th>
                <th style={styles.th}>가입일</th>
                <th style={styles.th}>추가결제</th>
              </tr>
            </thead>
            <tbody>{renderUserList()}</tbody>
          </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 모달들 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") return navigate("/");
            if (confirmFn) {
              const fn = confirmFn;
              setConfirmFn(null);
              fn();
            }
          }}
        />
      )}
      {infoModal && <PersonnelModal setInfoModal={setInfoModal} id={id} />}
      {payModal && (
        <PaymentModal
          setPayModal={setPayModal}
          userPk={userPk}
          onSuccess={() => {
            userList();
          }}
        />
      )}
      {isOpenCouponModal && (
        <CreateCouponModal
          mode="member"
          userPks={selectedUserIds}
          setIsOpenCouponModal={setIsOpenCouponModal}
          getList={userList}
        />
      )}

      {/* 반응형 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          .table-wrapper {
            -webkit-overflow-scrolling: touch;
          }
          
          .table-wrapper::-webkit-scrollbar {
            height: 8px;
          }
          
          .table-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        }
        
        @media (max-width: 640px) {
          .controls-container {
            justify-content: center;
          }
          
          .search-input {
            min-width: 150px !important;
          }
          
          .button-text {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Manage;